import React, { useMemo, Suspense, lazy } from 'react';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { Field, formValueSelector } from 'redux-form';
import FormField from '../../../atoms/Form/Field/FormField';
import getDummyData from '../../../../helpers/dummy';
import { useSelector } from 'react-redux';

const PdfOcrViewer = lazy(() => import('../../../atoms/PdfOcrViewer/PdfOcrViewer'));

const PdfOcrDataRender = ({
  input,
  id,
  meta: {
    initial,
    name,
  },
  ...props
}) => {
  const metadataAsText = useSelector((state) =>
    formValueSelector('contribute')(state, `${input.name}_metadata`),
  );

  const metadata = useMemo(() => {
    if (metadataAsText == null) {
      return null;
    }

    try {
      return JSON.parse(metadataAsText);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse Metadata', err);

      // If we have a message indicating that a JSON problem occurs at "position X",
      // we display it in console
      if (err.message.includes('position')) {
        const match = err.message.match(/position ([0-9]+)/);

        if (match) {
          const position = parseInt(match[1], 10);

          // eslint-disable-next-line no-console
          console.log(
            metadataAsText.slice(position - 20, Math.min(position + 20, metadataAsText.length)),
          );
        }
      }

      return null;
    }
  }, [metadataAsText]);

  if ((input.value || initial) === '') {
    return null;
  }

  return (
    <Suspense>
      <PdfOcrViewer
        id={id}
        name={name}
        link={input.value || initial}
        metadata={metadata}
        {...props}
      />
    </Suspense>
  );
};

PdfOcrDataRender.propTypes = {
  input: PropTypes.object,
  label: PropTypes.string,
  src: PropTypes.string,
  id: PropTypes.string,
  meta: PropTypes.shape({
    initial: PropTypes.string,
    name: PropTypes.string,
  }),
};

const PdfOcrData = ({
  name,
  label,
  fieldHoC: FieldHOC = Field,
  validation,
  isPreview,
  ...props
}) => {
  const id = useMemo(() => uuid(), [name]);
  const initial = useMemo(() => (isPreview ? getDummyData(validation.type) : ''), [validation]);

  return (
    <FormField label={label}>
      <FieldHOC
        name={name}
        id={id}
        component={PdfOcrDataRender}
        fixedMode={validation.fixedMode}
        meta={{ initial, name }}
        validation={validation}
        {...props}
      />
    </FormField>
  );
};

PdfOcrData.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  information: PropTypes.string,
  fieldHoC: PropTypes.func,
  validation: PropTypes.object,
  isPreview: PropTypes.bool,
};

export default PdfOcrData;
